/**
 * PlanoCard — card de plano atual CROSS M3.
 *
 * Exclusivo da tela Plano (BillingPage). Prefixo Plano conforme regra de isolamento.
 *
 * Renderiza:
 *   - cabecalho: badge de status + nome + tagline + bloco de preco
 *   - secao "O que esta incluido": lista de features com icone check
 *   - secao de ciclo/vencimento/metodo/valor — 4 colunas (espelha prototipo)
 *   - rodape: nota sem multa + botao "Ver planos" (deferred F5)
 *
 * Estado honesto:
 *   - preco: se nulo (billing diferido ate F5), exibe flag data-gap
 *   - planTagline: se ausente, exibe texto neutro de fallback
 *   - metodo: se nulo, exibe traco
 *   - nextBillingAt: se vazio, exibe "Nao agendada"
 *   - planPrice (coluna Valor): se nulo, exibe "—" (billing diferido F5)
 *
 * Props estritamente tipadas; sem estado local (todos os dados chegam via props).
 * Acessibilidade: todos os botoes tem aria-label; lista de features tem role semantico.
 */

import { t } from '../lib/i18n';

/** Feature fixa do plano — vem de i18n, nunca hardcoded aqui. */
interface PlanFeature {
  readonly label: string;
  readonly desc: string;
}

interface PlanoCardProps {
  readonly planName: string;
  readonly planTagline?: string | undefined;
  /** Fallback exibido quando a API nao retorna planTagline. Copy inline: escopo desta tarefa. */
  readonly planTaglineFallback?: string | undefined;
  readonly planPrice: string | null;
  readonly cycle: string;
  readonly nextBillingAt: string;
  readonly paymentMethod: string | null;
  readonly features: readonly PlanFeature[];
  readonly isActive?: boolean;
  readonly onViewPlans?: (() => void) | undefined;
}

function formatNextBilling(value: string, fallback: string): string {
  if (value.length === 0) return fallback;
  try {
    return new Date(value).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

export function PlanoCard({
  planName,
  planTagline,
  planTaglineFallback,
  planPrice,
  cycle,
  nextBillingAt,
  paymentMethod,
  features,
  isActive = true,
  onViewPlans,
}: PlanoCardProps): JSX.Element {
  const formattedNextBilling = formatNextBilling(nextBillingAt, t('billing.nextBillingNone'));

  /* Tagline: usa valor da API se disponivel; fallback neutro se ausente. */
  const resolvedTagline =
    planTagline !== undefined && planTagline.length > 0
      ? planTagline
      : (planTaglineFallback ?? null);

  return (
    <section className="plano-card" aria-label={t('billing.currentPlan')}>
      {/* Cabecalho: badge + nome + tagline + preco */}
      <div className="plano-card-head">
        <div>
          {isActive ? (
            <span className="plano-badge" role="status">
              {t('billing.planStatusActive')}
            </span>
          ) : null}
          <h2 className="plano-name">{planName}</h2>
          {resolvedTagline !== null ? <p className="plano-tagline">{resolvedTagline}</p> : null}
        </div>

        <div className="plano-price-block" aria-label={t('billing.priceLabel')}>
          {planPrice !== null ? (
            <>
              <div className="plano-price">{planPrice}</div>
              <div className="plano-price-cycle">{t('billing.priceCycle')}</div>
            </>
          ) : (
            <>
              <div className="plano-price" aria-hidden="true">
                —
              </div>
              <div className="plano-price-cycle">{t('billing.priceCycle')}</div>
              <span className="plano-price-flag">
                <span className="plano-flag-dot" aria-hidden="true" />
                {t('billing.priceDeferredNote')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Secao: features incluidas */}
      {features.length > 0 ? (
        <div className="plano-includes">
          <div className="plano-includes-title" aria-hidden="true">
            {t('billing.featuresTitle')}
          </div>
          <ul className="plano-feature-list" aria-label={t('billing.featuresTitle')}>
            {features.map((f) => (
              <li key={f.label} className="plano-feature-item">
                <span className="plano-feature-icon" aria-hidden="true">
                  check_circle
                </span>
                <div>
                  <div className="plano-feature-label">{f.label}</div>
                  <div className="plano-feature-desc">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Ciclo + vencimento + metodo + valor — 4 colunas conforme prototipo */}
      <div className="plano-billing-row">
        <div className="plano-billing-item">
          <span className="plano-billing-label">{t('billing.cycle')}</span>
          <span className="plano-billing-value">{cycle.length > 0 ? cycle : '—'}</span>
        </div>
        <div className="plano-billing-sep" aria-hidden="true" />
        <div className="plano-billing-item">
          <span className="plano-billing-label">{t('billing.nextBillingLabel')}</span>
          <span className={nextBillingAt.length > 0 ? 'plano-billing-value' : 'plano-billing-dash'}>
            {formattedNextBilling}
          </span>
        </div>
        <div className="plano-billing-sep" aria-hidden="true" />
        <div className="plano-billing-item">
          <span className="plano-billing-label">{t('billing.paymentMethodLabel')}</span>
          <span className={paymentMethod !== null ? 'plano-billing-value' : 'plano-billing-dash'}>
            {paymentMethod ?? '—'}
          </span>
        </div>
        <div className="plano-billing-sep" aria-hidden="true" />
        {/* Coluna Valor — "—" honesto enquanto billing diferido (F5) */}
        <div className="plano-billing-item">
          <span className="plano-billing-label">{t('billing.priceLabel')}</span>
          <span className={planPrice !== null ? 'plano-billing-value' : 'plano-billing-dash'}>
            {planPrice ?? '—'}
          </span>
        </div>
      </div>

      {/* Rodape */}
      <div className="plano-card-foot">
        <p className="plano-note">{t('billing.planCancelNote')}</p>
        <button
          type="button"
          className="plano-btn-ver-planos"
          onClick={onViewPlans}
          aria-label={t('billing.viewPlansAriaLabel')}
          disabled={onViewPlans === undefined}
        >
          <span className="plano-btn-icon" aria-hidden="true">
            open_in_new
          </span>
          {t('billing.viewPlans')}
        </button>
      </div>
    </section>
  );
}
